import { Injectable } from '@angular/core';
import { PageService } from './page.service';
import { Video } from '../models/video';
import { CrudService } from './crud.service';
import { ApplicationStateService } from './application-state.service';
import { VideoFactory } from '../models/factories/video-factory';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import axios from 'axios';

@Injectable({
    providedIn: 'root'
})
export class BookVideoService {

    private videos: Array<Video>;
    private indexToVideoMap: Map<number, Array<Video>> = new Map();

    constructor(private applicationStateService: ApplicationStateService,
                private crudService: CrudService,
                private factory: VideoFactory,
                private pageService: PageService,
                private sanitizer: DomSanitizer) {
    }

    setVideos(videos: Array<Video>): void {
        this.videos = [];
        this.indexToVideoMap = new Map();
        videos.forEach(entity => this.includeToIndexMap(entity));
    }

    /**
     * Similar to setVideos(videos) but in addition the passed values are transformed in forehand.
     * This method purpose is to do the transformation and the storing in just one for-Loop.
     * @param objects
     */
    setObjectsToVideos(objects: Array<any>): void {
        this.videos = [];
        this.indexToVideoMap = new Map();
        objects.forEach(entity => {
            this.includeToIndexMap(this.factory.buildFromObject(entity));
        });
    }

    getVideos(): Promise<Array<Video>> {

        return new Promise<Array<Video>>(resolve => {

            const thiz = this;

            if (!thiz.applicationStateService.bookConfig.bookPermissions.bookVideo.view) {
                thiz.videos = [];
            }

            if (thiz.videos) {
                resolve(thiz.videos);

                return;
            }

            thiz.crudService.getAllEntitiesOfBook(Video, this.factory)
                .then(videos => {
                    this.setVideos(videos as Array<Video>);
                    resolve(videos as Array<Video>);
                });
        });
    }

    getVideosOfPage(index: number): Promise<Array<Video>> {

        return new Promise(resolve => {
            this.getVideos()
                .then(() => {
                    if (this.indexToVideoMap.get(index)) {
                        resolve(this.indexToVideoMap.get(index));
                    } else {
                        resolve([]);
                    }
                });
        });
    }

    getVideoOfPages(pageIndex: number): Promise<Array<Video>> {

        return new Promise<Array<Video>>(resolve => {
            let videos = [];
            const promises = [];
            // Add the the promises of all search pages to the array
            for (let runningPageIndex = 0;
                 runningPageIndex < this.applicationStateService.currentViewMode.pagesToShow;
                 runningPageIndex++
            ) {
                promises.push(this.getVideosOfPage(pageIndex + runningPageIndex));
            }

            Promise.all(promises)
                .then(values => {
                    return new Promise(resolve => {
                        values.forEach(value => videos = videos.concat(value));
                        resolve(videos);
                    });
                });
        });
    }

    createVideo(video: Video): Promise<Video> {
        return this.crudService.create(video) as Promise<Video>;
    }

    updateVideo(video: Video): Promise<Video> {
        return this.crudService.update(video) as Promise<Video>;
    }

    deleteVideo(video: Video): Promise<boolean> {
        return this.crudService.remove(video) as Promise<boolean>;
    }

    logUsage(video: Video): Promise<boolean> {
        return this.crudService.log(video) as Promise<boolean>;
    }

    /**
     * Determine video url type and set preview image.
     * @param url
     */
    async getURLData(url: string): Promise<any> {
        const videoURL = new URL(url);
        const searchPart = videoURL.search;
        const searchParams = new URLSearchParams(searchPart);
        const urlHost = videoURL.host;
        const vParam = searchParams.get('v');
        const urlData: {
            url?: SafeResourceUrl;
            image?: string;
            type: 'iFrame' | 'custom';
        } = {
            type: 'iFrame'
        };

        if (urlHost.includes('youtu') && vParam) {
            urlData.url = this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${vParam}?autoplay=1`);
            urlData.image = `https://img.youtube.com/vi/${vParam}/sddefault.jpg`;
        } else if (urlHost.includes('vimeo')) {
            const result = await axios.get(`https://vimeo.com/api/oembed.json?url=${url}`);
            urlData.image = result.data.thumbnail_url;

            let autoplayParam = 'autoplay=1';
            if (!url.includes(autoplayParam)) {
                if (url.includes('?')) {
                    autoplayParam = `&${autoplayParam}`;
                } else {
                    autoplayParam = `?${autoplayParam}`;
                }
            }

            urlData.url = this.sanitizer.bypassSecurityTrustResourceUrl(url + autoplayParam);
        } else {
            urlData.url = this.sanitizer.bypassSecurityTrustResourceUrl(url);
            urlData.type = 'custom';
        }

        return urlData;
    }

    private includeToIndexMap(video: Video): void {
        const indexOfVideo = this.pageService.getIndexByPageId(video.pageId);
        video.index = indexOfVideo;
        this.videos.push(video);

        if (!this.indexToVideoMap.get(indexOfVideo)) {
            this.indexToVideoMap.set(indexOfVideo, [video]);
        } else {
            this.indexToVideoMap.get(indexOfVideo)
                .push(video);
        }
    }
}
